import { useEffect, useState } from "react";
import { api } from "../lib/api";
import CrudTable from "../components/CrudTable";
import { Modal } from "../components/ui";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine } from "recharts";
import { Plus, Trash2, Eye, Pencil } from "lucide-react";

const LIFECYCLE_STAGES = [
  "Extracción de materia prima",
  "Manufactura / Producción",
  "Transporte",
  "Uso / Operación",
  "Mantenimiento",
  "Fin de vida / Disposición",
];
const ASPECT_CLASS = ["Controlable", "Influenciable"];
const CONTROL_BY = ["Interno", "Proveedor", "Externo"];
const VERIF_RESULTS = ["Implementado", "Parcial", "No implementado"];
const WASTE_CLASS = ["RP", "RME", "RSU"];
const WASTE_DEST = ["Tratamiento", "Revalorización", "Disposición final"];
const MONTHS_FULL = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];
const MONTH_KEYS = ["jan","feb","mar","apr","may","jun","jul","aug","sep","oct","nov","dec"];
const YEARS = Array.from({ length: 11 }, (_, i) => 2020 + i);

export default function LifecycleTab() {
  const [selected, setSelected] = useState(null);
  return (
    <div data-testid="lifecycle-tab">
      <CrudTable
        collection="raw_materials"
        testIdPrefix="rawmat"
        onRowClick={(row) => setSelected(row)}
        fields={[
          { key: "name", label: "Material / Sustancia", required: true, full: true },
          { key: "kind", label: "Tipo", type: "select", options: ["Sólido","Líquido","Gaseoso","Mezcla"] },
          { key: "unit", label: "Unidad", type: "select", options: ["TON","KG","L","M³","Unidades","Otro"] },
          { key: "provider", label: "Proveedor principal" },
          { key: "area", label: "Área / proceso" },
          { key: "in_aia", label: "En Matriz AIA", type: "checkbox" },
          { key: "ytd_consumption", label: "Consumo YTD", type: "number" },
          { key: "main_stage", label: "Etapa principal" },
          { key: "main_aspect", label: "Aspecto principal" },
          { key: "intensity_unit", label: "Unidad producción" },
        ]}
      />
      <Modal open={!!selected} onClose={() => setSelected(null)} title={`Ficha — ${selected?.name || ""}`} size="xl" testId="rawmat-detail">
        {selected && <MaterialDetail material={selected} onReload={() => setSelected(null)} />}
      </Modal>
    </div>
  );
}

function MaterialDetail({ material }) {
  const [tab, setTab] = useState("stages");
  return (
    <div>
      <div className="tabs-bar">
        {[
          ["stages","Etapas y controles"],
          ["wastes","Residuos asociados"],
          ["consumption","Consumo mensual"],
        ].map(([k, lbl]) => (
          <button key={k} className={`tab ${tab===k?"active":""}`} onClick={() => setTab(k)} data-testid={`rawmat-tab-${k}`}>{lbl}</button>
        ))}
      </div>
      {tab === "stages" && <StagesPanel material={material} />}
      {tab === "wastes" && <WastesPanel material={material} />}
      {tab === "consumption" && <ConsumptionPanel material={material} />}
    </div>
  );
}

function StagesPanel({ material }) {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [show, setShow] = useState(false);

  async function load() {
    const { data } = await api.get("/data/raw_material_stages", { params: { raw_material_id: material.id } });
    setRows(data);
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [material.id]);

  function open(row = null) {
    setEditing(row || { raw_material_id: material.id, stage: "", description: "", aspect: "", aspect_class: "Controlable",
      control: "", control_by: "Interno", evidence_requested: false, evidence_url: "" });
    setShow(true);
  }
  async function save() {
    const p = { ...editing };
    delete p.id; delete p.tenant_id; delete p.created_at; delete p.updated_at;
    if (editing.id) await api.patch(`/data/raw_material_stages/${editing.id}`, p);
    else await api.post("/data/raw_material_stages", p);
    setShow(false); setEditing(null); load();
  }
  async function del(r) {
    if (!window.confirm("¿Eliminar etapa?")) return;
    await api.delete(`/data/raw_material_stages/${r.id}`);
    load();
  }

  const needsRequest = editing && (editing.control_by === "Proveedor" || editing.control_by === "Externo");

  return (
    <div>
      <div className="flex justify-between mb-2">
        <div className="text-sm text-dim">Una etapa por fila. Marca evidencia de solicitud cuando el control depende de un tercero.</div>
        <button className="btn btn-sm btn-primary" onClick={() => open()} data-testid="rawmat-stage-add"><Plus size={12} /> Nueva etapa</button>
      </div>
      <div className="table-wrap">
        <table className="table">
          <thead><tr><th>Etapa</th><th>Aspecto</th><th>Clasif.</th><th>Control</th><th>Aplica</th><th>Solicitado</th><th></th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} data-testid={`rawmat-stage-row-${r.id}`}>
                <td>{r.stage}</td>
                <td>{r.aspect}</td>
                <td><span className="badge">{r.aspect_class}</span></td>
                <td className="text-xs">{r.control}</td>
                <td>{r.control_by}</td>
                <td>
                  {(r.control_by === "Proveedor" || r.control_by === "Externo")
                    ? (r.evidence_requested ? <span className="badge badge-green">Sí</span> : <span className="badge badge-red">No</span>)
                    : <span className="text-dim text-xs">N/A</span>}
                </td>
                <td>
                  <div className="flex gap-1">
                    <button className="btn btn-sm" onClick={() => open(r)}><Pencil size={12} /></button>
                    <button className="btn btn-sm btn-danger" onClick={() => del(r)}><Trash2 size={12} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={7} className="text-center text-dim p-3">Sin etapas registradas.</td></tr>}
          </tbody>
        </table>
      </div>
      <Modal open={show} onClose={() => setShow(false)} title={editing?.id ? "Editar etapa" : "Nueva etapa"} size="lg" testId="rawmat-stage-modal">
        {editing && (
          <form onSubmit={(e) => { e.preventDefault(); save(); }} className="grid gap-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">Etapa *</label>
                <select className="select" required value={editing.stage} onChange={(e) => setEditing({...editing, stage: e.target.value})} data-testid="stage-select">
                  <option value="">--</option>
                  {LIFECYCLE_STAGES.map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="label">Aspecto ambiental</label>
                <input className="input" value={editing.aspect || ""} onChange={(e) => setEditing({...editing, aspect: e.target.value})} placeholder="Ej: emisiones de CO₂" />
              </div>
              <div>
                <label className="label">Clasificación</label>
                <select className="select" value={editing.aspect_class} onChange={(e) => setEditing({...editing, aspect_class: e.target.value})}>
                  {ASPECT_CLASS.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="label">¿Quién aplica el control?</label>
                <select className="select" value={editing.control_by} onChange={(e) => setEditing({...editing, control_by: e.target.value})}>
                  {CONTROL_BY.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="label">Descripción del control</label>
              <textarea className="textarea" rows={3} value={editing.control || ""} onChange={(e) => setEditing({...editing, control: e.target.value})} />
            </div>
            <div>
              <label className="label">Descripción de qué ocurre en esta etapa para este material</label>
              <textarea className="textarea" rows={2} value={editing.description || ""} onChange={(e) => setEditing({...editing, description: e.target.value})} />
            </div>
            {needsRequest && (
              <div className="card" style={{ background: "rgba(241,196,15,0.06)", border: "1px solid var(--warn)" }}>
                <div className="grid grid-cols-2 gap-3">
                  <label className="label flex items-center gap-2">
                    <input type="checkbox" checked={!!editing.evidence_requested} onChange={(e) => setEditing({...editing, evidence_requested: e.target.checked})} data-testid="stage-evidence-requested" />
                    ¿Se solicitó al proveedor/externo?
                  </label>
                  <div>
                    <label className="label">Evidencia de solicitud (URL/CCE)</label>
                    <input className="input" value={editing.evidence_url || ""} onChange={(e) => setEditing({...editing, evidence_url: e.target.value})} placeholder="URL o # CCE de carta/correo/contrato" />
                  </div>
                </div>
                {!editing.evidence_requested && (
                  <div className="text-xs mt-2" style={{ color: "var(--warn)" }}>
                    ⚠ Al guardar como "no solicitado", se generará alerta en el dashboard.
                  </div>
                )}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button type="button" className="btn" onClick={() => setShow(false)}>Cancelar</button>
              <button type="submit" className="btn btn-primary" data-testid="stage-save">Guardar</button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}

function WastesPanel({ material }) {
  return (
    <CrudTable
      collection="raw_material_wastes"
      filters={{ raw_material_id: material.id }}
      parent={{ id: material.id, field: "raw_material_id" }}
      testIdPrefix={`rawmat-waste-${material.id}`}
      fields={[
        { key: "waste_type", label: "Tipo de residuo", required: true },
        { key: "classification", label: "Clasificación", type: "select", options: WASTE_CLASS },
        { key: "qty_per_unit", label: "Cantidad por unidad consumida", type: "number" },
        { key: "destination", label: "Destino", type: "select", options: WASTE_DEST },
        { key: "provider", label: "Proveedor de manejo" },
        { key: "waste_log_id", label: "Link a Gestión Residuos (ID)" },
      ]}
    />
  );
}

function ConsumptionPanel({ material }) {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [row, setRow] = useState(null);
  const [allRows, setAllRows] = useState([]);

  async function load() {
    const { data } = await api.get("/data/raw_material_consumption", { params: { raw_material_id: material.id } });
    setAllRows(data);
    const yr = data.find((r) => r.year === year);
    setRow(yr || { raw_material_id: material.id, year, ...Object.fromEntries(MONTH_KEYS.map((k) => [k, 0])), cost_mxp: 0 });
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [material.id, year]);

  async function save() {
    const payload = { ...row };
    delete payload.id; delete payload.tenant_id; delete payload.created_at; delete payload.updated_at;
    if (row.id) await api.patch(`/data/raw_material_consumption/${row.id}`, payload);
    else await api.post("/data/raw_material_consumption", payload);
    load();
  }

  const ytd = row ? MONTH_KEYS.reduce((a, k) => a + (Number(row[k]) || 0), 0) : 0;
  const histAvg = allRows.length > 1
    ? allRows.filter((r) => r.year !== year).reduce((acc, r) => acc + MONTH_KEYS.reduce((a, k) => a + (r[k] || 0), 0), 0) / Math.max(1, allRows.length - 1)
    : 0;
  const chartData = row ? MONTHS_FULL.map((m, i) => ({ month: m, value: row[MONTH_KEYS[i]] || 0 })) : [];
  const intensity = material.intensity_unit ? (ytd / (material.intensity_value || 1)).toFixed(2) : null;

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="flex gap-2 items-center">
          <label className="label" style={{ margin: 0 }}>Año:</label>
          <select className="select" value={year} onChange={(e) => setYear(Number(e.target.value))} style={{ width: 110 }} data-testid="rawmat-year-select">
            {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div className="text-sm">
          <span className="badge badge-green">YTD: {ytd.toFixed(2)} {material.unit || ""}</span>
          {!!histAvg && <span className="badge ml-2">Promedio hist: {histAvg.toFixed(2)}</span>}
          {intensity && <span className="badge badge-blue ml-2">Intensidad: {intensity} {material.unit}/{material.intensity_unit}</span>}
        </div>
      </div>

      {row && (
        <div className="table-wrap mb-3">
          <table className="table">
            <thead><tr>{MONTHS_FULL.map((m) => <th key={m}>{m}</th>)}<th>Costo $MXP</th></tr></thead>
            <tbody>
              <tr>
                {MONTH_KEYS.map((k) => (
                  <td key={k}>
                    <input type="number" className="input" style={{ padding: 4, fontSize: 12, width: 80 }} value={row[k] ?? 0}
                      onChange={(e) => setRow({...row, [k]: Number(e.target.value)})} data-testid={`rawmat-month-${k}`} />
                  </td>
                ))}
                <td>
                  <input type="number" className="input" style={{ padding: 4, fontSize: 12, width: 110 }} value={row.cost_mxp ?? 0}
                    onChange={(e) => setRow({...row, cost_mxp: Number(e.target.value)})} />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      <div className="flex justify-end mb-3">
        <button className="btn btn-primary" onClick={save} data-testid="rawmat-consumption-save">Guardar consumo {year}</button>
      </div>

      <ResponsiveContainer width="100%" height={240}>
        <BarChart data={chartData}>
          <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />
          <XAxis dataKey="month" stroke="var(--text-dim)" fontSize={11} interval={0} />
          <YAxis stroke="var(--text-dim)" fontSize={11} />
          <Tooltip contentStyle={{ background: "var(--bg-2)", border: "1px solid var(--border-2)", borderRadius: 8 }} />
          <Legend />
          <Bar dataKey="value" fill="#1d9e75" name={`Consumo ${year} (${material.unit || ""})`} />
          {!!histAvg && <ReferenceLine y={histAvg / 12} stroke="#f1c40f" strokeDasharray="3 3" label={{ value: "Promedio hist (mes)", position: "right", fill: "#f1c40f", fontSize: 10 }} />}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
